<script setup>
import { h, computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStorage } from '@vueuse/core';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { createSidebarActionHandler } from './sidebarActionHandler';
import { useOpsNoticesSync } from 'dashboard/components-next/ops-notices/useOpsNoticesSync';

import SidebarIconButton from './SidebarIconButton.vue';
import SidebarPanel from './SidebarPanel.vue';
import ChannelLeaf from './ChannelLeaf.vue';

import {
  INBOX_ACCESS_PERMISSIONS,
  SETTINGS_ACCESS_PERMISSIONS,
  AGENTS_ACCESS_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
  CONTACT_PERMISSIONS,
  TELEPHONY_REPORTS_ACCESS_PERMISSIONS,
  WHATSAPP_VOICE_REPORTS_ACCESS_PERMISSIONS,
  KANBAN_ACCESS_PERMISSIONS,
  KANBAN_MANAGE_PERMISSIONS,
  CHAMADOS_VIEW_PERMISSIONS,
  MASSIVA_ACCESS_PERMISSIONS,
} from 'dashboard/constants/permissions.js';
import { kanbanRoutePermissions } from 'dashboard/helper/kanbanPermissions';
import { useKanbanPermissions } from 'dashboard/composables/useKanbanPermissions';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  isMobileSidebarOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['closeMobileSidebar']);

const { accountScopedRoute } = useAccount();
const store = useStore();
const route = useRoute();
const { t } = useI18n();
const { canViewKanbanAnalytics } = useKanbanPermissions();
useOpsNoticesSync();

const selectedItemName = useStorage(
  'sidebar-selected-section',
  null,
  localStorage
);

provideSidebarContext({
  expandedItem: ref(null),
  setExpandedItem: () => {},
  isCollapsed: computed(() => false),
  toggleSidebar: () => {},
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isAsteriskTelephonyEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.ASTERISK_TELEPHONY
  )
);

const isStudioEnabled = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.AI)
);

const isWhatsAppSipEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.WHATSAPP_SIP_TELEPHONY
  )
);

const isKanbanEnabled = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.KANBAN)
);

const isCaptainEnabled = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);

const isSupervisorLivePanelEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.SUPERVISOR_LIVE_PANEL
  )
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    icon: 'i-lucide-user',
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    icon: 'i-lucide-tag',
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    icon: 'i-lucide-inbox',
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    icon: 'i-lucide-users-round',
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
  {
    name: 'Reports Round Robin',
    label: t('SIDEBAR.REPORTS_ROUND_ROBIN'),
    icon: 'i-lucide-rotate-cw',
    to: accountScopedRoute('round_robin_dashboard'),
    activeOn: ['round_robin_dashboard'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const currentAccount = useMapGetter('getCurrentAccount');

const filteredLabels = computed(() => {
  const allLabels = labels.value || [];
  const userTeams = teams.value || [];

  if (userTeams.length === 0 || allLabels.length === 0) {
    return allLabels;
  }

  const teamLabelIds = new Set();
  userTeams.forEach(team => {
    if (team.label_ids && Array.isArray(team.label_ids)) {
      team.label_ids.forEach(labelId => teamLabelIds.add(labelId));
    }
  });

  if (teamLabelIds.size === 0) {
    return allLabels;
  }

  return allLabels.filter(label => teamLabelIds.has(label.id));
});

const hasMenuPermission = (requiredPermissions = []) => {
  const userPermissions = currentAccount.value?.permissions || [];

  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  if (userPermissions.includes('administrator')) {
    return true;
  }

  if (userPermissions.length === 0) {
    return false;
  }

  return requiredPermissions.some(permission =>
    userPermissions.includes(permission)
  );
};

// Número de chamados com novidade do Gestão no ícone Chamados — só pra quem
// vê a página (o endpoint recusa os demais). watch, não onMounted: no mount a
// conta (e as permissões) ainda não carregou.
const canSeeChamados = computed(() =>
  hasMenuPermission([CHAMADOS_VIEW_PERMISSIONS])
);
watch(
  canSeeChamados,
  allowed => {
    if (allowed) store.dispatch('studioChamados/enableNews');
  },
  { immediate: true }
);

const menuItems = computed(() => {
  const items = [
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      requiredPermissions: [INBOX_ACCESS_PERMISSIONS],
      getterKeys: {
        badge: 'notifications/getHasUnreadNotifications',
      },
    },
    {
      name: 'Ops Notices',
      label: t('OPS_NOTICES.SIDEBAR_LABEL'),
      icon: 'i-lucide-siren',
      to: accountScopedRoute('ops_notices_index'),
      getterKeys: {
        count: 'opsNotices/getBadge',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          icon: 'i-lucide-messages-square',
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          icon: 'i-lucide-at-sign',
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          icon: 'i-lucide-clock-alert',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          actions: [
            {
              label: t('SIDEBAR.NEW_FOLDER'),
              icon: 'i-lucide-plus',
              action: 'new-folder',
            },
          ],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
            actions: [
              {
                label: t('SIDEBAR.EDIT_FOLDER'),
                icon: 'i-lucide-pen',
                action: 'edit-folder',
                value: view.id,
              },
            ],
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          actions: [
            {
              label: t('SIDEBAR.NEW_TEAM'),
              icon: 'i-lucide-plus',
              to: accountScopedRoute('settings_teams_new'),
            },
          ],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
            actions: [
              {
                label: t('SIDEBAR.CONFIGURE_TEAM'),
                icon: 'i-lucide-settings',
                to: accountScopedRoute('settings_teams_edit', {
                  teamId: team.id,
                }),
              },
            ],
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          actions: [
            {
              label: t('SIDEBAR.NEW_INBOX'),
              icon: 'i-lucide-plus',
              to: accountScopedRoute('settings_inbox_new', { page: 'new' }),
            },
          ],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            actions: [
              {
                label: t('SIDEBAR.CONFIGURE_INBOX'),
                icon: 'i-lucide-settings',
                to: accountScopedRoute('settings_inbox_show', {
                  inboxId: inbox.id,
                }),
              },
            ],
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          actions: [
            {
              label: t('SIDEBAR.MANAGE_LABELS'),
              icon: 'i-lucide-plus',
              to: accountScopedRoute('labels_list'),
            },
          ],
          children: filteredLabels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
            actions: [
              {
                label: t('SIDEBAR.EDIT_LABEL'),
                icon: 'i-lucide-settings',
                to: accountScopedRoute('labels_list', {}, { edit: label.id }),
              },
            ],
          })),
        },
      ],
    },
    ...(isCaptainEnabled.value
      ? [
          {
            name: 'Captain',
            icon: 'i-woot-captain',
            label: t('SIDEBAR.CAPTAIN'),
            requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
            children: [
              {
                name: 'Assistants',
                label: t('SIDEBAR.CAPTAIN_ASSISTANTS'),
                to: accountScopedRoute('captain_assistants_index'),
              },
              {
                name: 'Documents',
                label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
                to: accountScopedRoute('captain_documents_index'),
              },
              {
                name: 'Responses',
                label: t('SIDEBAR.CAPTAIN_RESPONSES'),
                to: accountScopedRoute('captain_responses_index'),
              },
            ],
          },
        ]
      : []),
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      requiredPermissions: [CONTACT_PERMISSIONS],
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
          actions: [
            {
              label: t('SIDEBAR.NEW_CONTACT'),
              icon: 'i-lucide-plus',
              action: 'new-contact',
            },
          ],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          icon: 'i-lucide-user-check',
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      requiredPermissions: [REPORTS_PERMISSIONS],
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          icon: 'i-lucide-activity',
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          icon: 'i-lucide-message-circle',
          to: accountScopedRoute('conversation_reports'),
        },
        {
          name: 'Report Operations',
          label: t('SIDEBAR.REPORTS_OPERATIONS'),
          icon: 'i-lucide-layout-dashboard',
          to: accountScopedRoute('account_operations_reports'),
        },
        {
          name: 'Reports Ad Leads',
          label: t('SIDEBAR.REPORTS_AD_LEADS'),
          icon: 'i-lucide-megaphone',
          to: accountScopedRoute('ad_leads_reports'),
        },
        {
          name: 'Reports Meta Costs',
          label: t('SIDEBAR.REPORTS_META_COSTS'),
          icon: 'i-lucide-receipt',
          to: accountScopedRoute('meta_costs_reports'),
        },
        {
          name: 'Reports Pauses',
          label: t('SIDEBAR.REPORTS_PAUSES'),
          icon: 'i-lucide-pause',
          to: accountScopedRoute('pause_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          icon: 'i-lucide-smile',
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          icon: 'i-lucide-timer',
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('bot_reports'),
        },
        {
          name: 'Reports Custom',
          label: t('SIDEBAR.REPORTS_CUSTOM'),
          icon: 'i-lucide-chart-spline',
          to: accountScopedRoute('custom_reports'),
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
      children: [
        {
          name: 'WhatsApp App',
          label: t('SIDEBAR.WHATSAPP_APP'),
          icon: 'i-lucide-message-circle',
          to: accountScopedRoute('campaigns_whatsapp_app_home'),
          activeOn: [
            'campaigns_whatsapp_app_home',
            'campaigns_whatsapp_app_schedule',
            'campaigns_whatsapp_app_home_create',
            'campaigns_whatsapp_app_configure_list',
            'campaigns_whatsapp_app_schedule_step',
            'campaigns_whatsapp_app_finish',
            'campaigns_whatsapp_app_schedule_deliveries',
            'campaigns_whatsapp_app_schedule_list',
            'campaigns_whatsapp_app_templates',
            'campaigns_whatsapp_app_template_edit',
          ],
          children: [
            {
              name: 'Template Builder App',
              label: t('SIDEBAR.WHATSAPP_APP_TEMPLATES'),
              icon: 'i-lucide-layout-template',
              to: accountScopedRoute('campaigns_whatsapp_app_templates'),
              activeOn: [
                'campaigns_whatsapp_app_templates',
                'campaigns_whatsapp_app_template_edit',
              ],
            },
            {
              name: 'Home Campaign App',
              label: t('SIDEBAR.WHATSAPP_APP_HOME'),
              icon: 'i-lucide-send',
              to: accountScopedRoute('campaigns_whatsapp_app_home'),
              activeOn: [
                'campaigns_whatsapp_app_home',
                'campaigns_whatsapp_app_home_create',
              ],
            },
            {
              name: 'Schedule Campaign App',
              label: t('SIDEBAR.WHATSAPP_APP_SCHEDULE'),
              icon: 'i-lucide-calendar-clock',
              to: accountScopedRoute('campaigns_whatsapp_app_schedule'),
              activeOn: ['campaigns_whatsapp_app_schedule'],
            },
          ],
        },
        {
          name: 'Whatsapp Oficial',
          label: t('SIDEBAR.WHATSAPP_OFFICIAL'),
          icon: 'i-lucide-badge-check',
          to: accountScopedRoute('campaigns_whatsapp_official_home'),
          activeOn: [
            'campaigns_whatsapp_official_home',
            'campaigns_whatsapp_official_schedule',
            'campaigns_whatsapp_official_create_template',
            'campaigns_whatsapp_official_home_create',
            'campaigns_whatsapp_official_configure_list',
            'campaigns_whatsapp_official_schedule_step',
            'campaigns_whatsapp_official_finish',
            'campaigns_whatsapp_official_schedule_deliveries',
            'campaigns_whatsapp_official_schedule_list',
            'campaigns_whatsapp_official_builder',
          ],
          children: [
            // Um item por INTENÇÃO, nome = trabalho da página: gerenciar
            // templates (catálogo) · criar campanha (builder) · acompanhar
            // (agendamentos). A lista de templates NÃO é porta de criação.
            {
              name: 'Create Template',
              label: t('SIDEBAR.WHATSAPP_OFFICIAL_CREATE_TEMPLATE'),
              icon: 'i-lucide-layout-template',
              to: accountScopedRoute(
                'campaigns_whatsapp_official_create_template'
              ),
              activeOn: ['campaigns_whatsapp_official_create_template'],
            },
            {
              name: 'Templates',
              label: t('SIDEBAR.WHATSAPP_OFFICIAL_HOME'),
              icon: 'i-lucide-layout-grid',
              to: accountScopedRoute('campaigns_whatsapp_official_home'),
              activeOn: ['campaigns_whatsapp_official_home'],
            },
            {
              name: 'New Campaign',
              label: t('SIDEBAR.WHATSAPP_OFFICIAL_BUILDER'),
              icon: 'i-lucide-plus-circle',
              to: accountScopedRoute('campaigns_whatsapp_official_builder'),
              activeOn: ['campaigns_whatsapp_official_builder'],
            },
            {
              name: 'Schedule Campaign',
              label: t('SIDEBAR.WHATSAPP_OFFICIAL_SCHEDULE'),
              icon: 'i-lucide-calendar-clock',
              to: accountScopedRoute('campaigns_whatsapp_official_schedule'),
              activeOn: ['campaigns_whatsapp_official_schedule'],
            },
          ],
        },
      ],
    },
    ...(isAsteriskTelephonyEnabled.value
      ? [
          {
            name: 'Telephony',
            label: t('SIDEBAR.TELEPHONY'),
            icon: 'i-lucide-phone',
            requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
            children: [
              {
                name: 'Telephony Dashboard',
                label: t('SIDEBAR.TELEPHONY_DASHBOARD'),
                icon: 'i-lucide-layout-dashboard',
                to: accountScopedRoute('telephony_dashboard'),
                activeOn: ['telephony_dashboard'],
                requiredPermissions: [TELEPHONY_REPORTS_ACCESS_PERMISSIONS],
              },
              {
                name: 'Trunks',
                label: t('SIDEBAR.TELEPHONY_TRUNKS'),
                icon: 'i-lucide-cable',
                to: accountScopedRoute('telephony_trunks'),
                activeOn: [
                  'telephony_trunks',
                  'telephony_trunk_new',
                  'telephony_trunk_edit',
                ],
                actions: [
                  {
                    label: t('SIDEBAR.NEW_TRUNK'),
                    icon: 'i-lucide-plus',
                    to: accountScopedRoute('telephony_trunk_new'),
                  },
                ],
              },
              {
                name: 'Extensions',
                label: t('SIDEBAR.TELEPHONY_EXTENSIONS'),
                icon: 'i-lucide-phone',
                to: accountScopedRoute('telephony_extensions'),
                activeOn: [
                  'telephony_extensions',
                  'telephony_extension_new',
                  'telephony_extension_edit',
                ],
                actions: [
                  {
                    label: t('SIDEBAR.NEW_EXTENSION'),
                    icon: 'i-lucide-plus',
                    to: accountScopedRoute('telephony_extension_new'),
                  },
                ],
              },
              {
                name: 'DIDs',
                label: t('SIDEBAR.TELEPHONY_DIDS'),
                icon: 'i-lucide-phone-incoming',
                to: accountScopedRoute('telephony_dids'),
                activeOn: [
                  'telephony_dids',
                  'telephony_did_new',
                  'telephony_did_edit',
                ],
                actions: [
                  {
                    label: t('SIDEBAR.NEW_DID'),
                    icon: 'i-lucide-plus',
                    to: accountScopedRoute('telephony_did_new'),
                  },
                ],
              },
              {
                name: 'Queues',
                label: t('SIDEBAR.TELEPHONY_QUEUES'),
                icon: 'i-lucide-users',
                to: accountScopedRoute('telephony_queues'),
                activeOn: [
                  'telephony_queues',
                  'telephony_queue_new',
                  'telephony_queue_edit',
                  'telephony_queue_members',
                ],
                actions: [
                  {
                    label: t('SIDEBAR.NEW_QUEUE'),
                    icon: 'i-lucide-plus',
                    to: accountScopedRoute('telephony_queue_new'),
                  },
                ],
              },
              {
                name: 'Queue Routes',
                label: t('SIDEBAR.TELEPHONY_QUEUE_ROUTES'),
                icon: 'i-lucide-route',
                to: accountScopedRoute('telephony_queue_routes'),
                activeOn: [
                  'telephony_queue_routes',
                  'telephony_queue_route_new',
                  'telephony_queue_route_edit',
                ],
                actions: [
                  {
                    label: t('SIDEBAR.NEW_QUEUE_ROUTE'),
                    icon: 'i-lucide-plus',
                    to: accountScopedRoute('telephony_queue_route_new'),
                  },
                ],
              },
              {
                name: 'Call Records',
                label: t('SIDEBAR.TELEPHONY_CALL_RECORDS'),
                icon: 'i-lucide-history',
                to: accountScopedRoute('telephony_call_records'),
                activeOn: ['telephony_call_records'],
                requiredPermissions: [TELEPHONY_REPORTS_ACCESS_PERMISSIONS],
              },
              {
                name: 'Pause Report',
                label: t('SIDEBAR.TELEPHONY_PAUSE_REPORT'),
                icon: 'i-lucide-pause',
                // Relatório único (Relatórios › Pausas) — atalho daqui. `shortcut`:
                // a página é de Relatórios, então só aquele ícone acende.
                to: accountScopedRoute('pause_reports'),
                shortcut: true,
                requiredPermissions: [TELEPHONY_REPORTS_ACCESS_PERMISSIONS],
              },
              {
                name: 'IVR',
                label: 'URA Interativa',
                icon: 'i-lucide-list-tree',
                to: accountScopedRoute('telephony_ivr'),
                activeOn: [
                  'telephony_ivr',
                  'telephony_ivr_builder_new',
                  'telephony_ivr_builder_edit',
                ],
              },
              ...(isSupervisorLivePanelEnabled.value
                ? [
                    {
                      name: 'Operational Panel',
                      label: t('SIDEBAR.TELEPHONY_OPERATIONAL_PANEL'),
                      icon: 'i-lucide-monitor',
                      to: accountScopedRoute('telephony_operational_panel'),
                      activeOn: ['telephony_operational_panel'],
                    },
                    {
                      name: 'Supervisor Panel',
                      label: t('SIDEBAR.TELEPHONY_SUPERVISOR_PANEL'),
                      icon: 'i-lucide-gauge',
                      to: accountScopedRoute('telephony_supervisor_panel'),
                      activeOn: ['telephony_supervisor_panel'],
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(isWhatsAppSipEnabled.value
      ? [
          {
            name: 'WhatsApp Voice',
            label: t('SIDEBAR.WHATSAPP_VOICE'),
            icon: 'i-lucide-headset',
            requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
            children: [
              {
                name: 'Extensions',
                label: t('SIDEBAR.WHATSAPP_VOICE_EXTENSIONS'),
                icon: 'i-lucide-phone',
                to: accountScopedRoute('whatsapp_sip_extensions'),
                activeOn: [
                  'whatsapp_sip_extensions',
                  'whatsapp_sip_extension_new',
                  'whatsapp_sip_extension_edit',
                ],
                actions: [
                  {
                    label: t('SIDEBAR.NEW_EXTENSION'),
                    icon: 'i-lucide-plus',
                    to: accountScopedRoute('whatsapp_sip_extension_new'),
                  },
                ],
              },
              {
                name: 'Queues',
                label: t('SIDEBAR.WHATSAPP_VOICE_QUEUES'),
                icon: 'i-lucide-users',
                to: accountScopedRoute('whatsapp_sip_queues'),
                activeOn: [
                  'whatsapp_sip_queues',
                  'whatsapp_sip_queue_new',
                  'whatsapp_sip_queue_edit',
                ],
                actions: [
                  {
                    label: t('SIDEBAR.NEW_QUEUE'),
                    icon: 'i-lucide-plus',
                    to: accountScopedRoute('whatsapp_sip_queue_new'),
                  },
                ],
              },
              {
                name: 'DIDs',
                label: t('SIDEBAR.WHATSAPP_VOICE_DIDS'),
                icon: 'i-lucide-phone-incoming',
                to: accountScopedRoute('whatsapp_sip_dids'),
                activeOn: [
                  'whatsapp_sip_dids',
                  'whatsapp_sip_did_new',
                  'whatsapp_sip_did_edit',
                ],
                actions: [
                  {
                    label: t('SIDEBAR.NEW_DID'),
                    icon: 'i-lucide-plus',
                    to: accountScopedRoute('whatsapp_sip_did_new'),
                  },
                ],
              },
              {
                name: 'Trunks',
                label: t('SIDEBAR.WHATSAPP_VOICE_TRUNKS'),
                icon: 'i-lucide-cable',
                to: accountScopedRoute('whatsapp_sip_trunks'),
                activeOn: [
                  'whatsapp_sip_trunks',
                  'whatsapp_sip_trunk_new',
                  'whatsapp_sip_trunk_edit',
                ],
                actions: [
                  {
                    label: t('SIDEBAR.NEW_TRUNK'),
                    icon: 'i-lucide-plus',
                    to: accountScopedRoute('whatsapp_sip_trunk_new'),
                  },
                ],
              },
              {
                name: 'IVR',
                label: t('SIDEBAR.WHATSAPP_VOICE_IVR'),
                icon: 'i-lucide-list-tree',
                to: accountScopedRoute('whatsapp_calling_ivr'),
                activeOn: [
                  'whatsapp_calling_ivr',
                  'whatsapp_calling_ivr_builder_new',
                  'whatsapp_calling_ivr_builder_edit',
                ],
              },
              {
                name: 'Holidays',
                label: t('SIDEBAR.WHATSAPP_VOICE_HOLIDAYS'),
                icon: 'i-lucide-calendar-off',
                to: accountScopedRoute('whatsapp_sip_holidays'),
                activeOn: [
                  'whatsapp_sip_holidays',
                  'whatsapp_sip_holiday_new',
                  'whatsapp_sip_holiday_edit',
                ],
                actions: [
                  {
                    label: t('SIDEBAR.NEW_HOLIDAY'),
                    icon: 'i-lucide-plus',
                    to: accountScopedRoute('whatsapp_sip_holiday_new'),
                  },
                ],
              },
              {
                name: 'CallReports',
                label: t('SIDEBAR.WHATSAPP_VOICE_CALL_REPORTS'),
                icon: 'i-lucide-bar-chart-3',
                to: accountScopedRoute('whatsapp_sip_call_reports'),
                activeOn: ['whatsapp_sip_call_reports'],
                requiredPermissions: [
                  WHATSAPP_VOICE_REPORTS_ACCESS_PERMISSIONS,
                ],
              },
              {
                name: 'PauseReport',
                label: t('SIDEBAR.WHATSAPP_VOICE_PAUSE_REPORT'),
                icon: 'i-lucide-pause',
                // Relatório único (Relatórios › Pausas) — atalho daqui. `shortcut`:
                // a página é de Relatórios, então só aquele ícone acende.
                to: accountScopedRoute('pause_reports'),
                shortcut: true,
                requiredPermissions: [
                  WHATSAPP_VOICE_REPORTS_ACCESS_PERMISSIONS,
                ],
              },
              {
                name: 'AutoDialer',
                label: t('SIDEBAR.WHATSAPP_VOICE_AUTODIALER'),
                icon: 'i-lucide-phone-outgoing',
                to: accountScopedRoute('whatsapp_sip_autodialer_ai_campaigns'),
                activeOn: ['whatsapp_sip_autodialer_ai_campaigns'],
                children: [
                  {
                    name: 'AutoDialerAICampaigns',
                    label: t('SIDEBAR.WHATSAPP_VOICE_AUTODIALER_AI_CAMPAIGNS'),
                    icon: 'i-lucide-bot',
                    to: accountScopedRoute(
                      'whatsapp_sip_autodialer_ai_campaigns'
                    ),
                    activeOn: ['whatsapp_sip_autodialer_ai_campaigns'],
                  },
                ],
              },
              {
                name: 'Supervisor',
                label: t('SIDEBAR.WHATSAPP_VOICE_SUPERVISOR'),
                icon: 'i-lucide-monitor-dot',
                to: accountScopedRoute('whatsapp_sip_supervisor'),
                activeOn: [
                  'whatsapp_sip_supervisor',
                  'whatsapp_sip_supervisor_agents',
                  'whatsapp_sip_supervisor_live_calls',
                  'whatsapp_sip_supervisor_metrics',
                ],
                requiredPermissions: [
                  WHATSAPP_VOICE_REPORTS_ACCESS_PERMISSIONS,
                ],
                children: [
                  {
                    name: 'SupervisorDashboard',
                    label: t('SIDEBAR.WHATSAPP_VOICE_SUPERVISOR_DASHBOARD'),
                    icon: 'i-lucide-layout-dashboard',
                    to: accountScopedRoute('whatsapp_sip_supervisor'),
                    activeOn: ['whatsapp_sip_supervisor'],
                  },
                  {
                    name: 'SupervisorAgents',
                    label: t('SIDEBAR.WHATSAPP_VOICE_SUPERVISOR_AGENTS'),
                    icon: 'i-lucide-headphones',
                    to: accountScopedRoute('whatsapp_sip_supervisor_agents'),
                    activeOn: ['whatsapp_sip_supervisor_agents'],
                  },
                  {
                    name: 'SupervisorLiveCalls',
                    label: t('SIDEBAR.WHATSAPP_VOICE_SUPERVISOR_LIVE_CALLS'),
                    icon: 'i-lucide-radio',
                    to: accountScopedRoute(
                      'whatsapp_sip_supervisor_live_calls'
                    ),
                    activeOn: ['whatsapp_sip_supervisor_live_calls'],
                  },
                  {
                    name: 'SupervisorMetrics',
                    label: t('SIDEBAR.WHATSAPP_VOICE_SUPERVISOR_METRICS'),
                    icon: 'i-lucide-activity',
                    to: accountScopedRoute('whatsapp_sip_supervisor_metrics'),
                    activeOn: ['whatsapp_sip_supervisor_metrics'],
                  },
                ],
              },
            ],
          },
        ]
      : []),
    ...(isStudioEnabled.value
      ? [
          {
            name: 'Studio',
            label: 'AI Studio',
            icon: 'i-lucide-audio-waveform',
            requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
            children: [
              {
                name: 'Studio Settings',
                label: 'Configurações',
                icon: 'i-lucide-settings',
                to: accountScopedRoute('studio_settings'),
                activeOn: ['studio_settings'],
              },
              {
                name: 'Studio Projects',
                label: 'Projetos',
                icon: 'i-lucide-folder-kanban',
                to: accountScopedRoute('studio_projects'),
                // Playground vive dentro do projeto (botão "Testar fluxo"); a
                // aba do projeto fica ativa também na rota do playground.
                activeOn: ['studio_projects', 'studio_project_playground'],
              },
              // Oculto da sidebar por enquanto (a pedido). A rota `studio_templates`
              // e o uso interno (nó "Resposta por template" no fluxo) seguem ativos —
              // reexibir = descomentar este item.
              // {
              //   name: 'Studio Templates',
              //   label: 'Templates',
              //   icon: 'i-lucide-file-text',
              //   to: accountScopedRoute('studio_templates'),
              //   activeOn: ['studio_templates', 'studio_template_edit'],
              //   actions: [
              //     {
              //       label: t('SIDEBAR.NEW_TEMPLATE'),
              //       icon: 'i-lucide-plus',
              //       to: accountScopedRoute('studio_template_edit', {
              //         templateId: 'new',
              //       }),
              //     },
              //   ],
              // },
              {
                name: 'Studio Massiva',
                label: t('AI.MASSIVA.SIDEBAR_LABEL'),
                icon: 'i-lucide-wifi-off',
                to: accountScopedRoute('studio_massiva'),
                activeOn: ['studio_massiva'],
                requiredPermissions: [MASSIVA_ACCESS_PERMISSIONS],
                children: [
                  // Queda que ACONTECEU vs manutenção que VAI acontecer — mesmo
                  // domínio, tempos opostos. Por isso aninhada, não item solto.
                  {
                    name: 'Studio Massiva Manutenção',
                    label: t('AI.MASSIVA.MAINTENANCE.SIDEBAR_LABEL'),
                    to: accountScopedRoute('studio_massiva_maintenance'),
                    activeOn: ['studio_massiva_maintenance'],
                  },
                  {
                    name: 'Studio Massiva Comunicados',
                    label: t('AI.MASSIVA.NOTICE.SIDEBAR_LABEL'),
                    to: accountScopedRoute('studio_massiva_notices'),
                    activeOn: ['studio_massiva_notices'],
                  },
                ],
              },
              {
                name: 'Studio Commercial',
                label: t('AI.COMMERCIAL.SIDEBAR_LABEL'),
                icon: 'i-lucide-badge-dollar-sign',
                to: accountScopedRoute('studio_commercial_settings'),
                activeOn: ['studio_commercial', 'studio_commercial_settings'],
                children: [
                  // Configurar e acompanhar são assuntos de naturezas
                  // diferentes — mesmo desenho da Massiva (queda × manutenção).
                  {
                    name: 'Studio Commercial Sales',
                    label: t('AI.COMMERCIAL.SALES.SIDEBAR_LABEL'),
                    to: accountScopedRoute('studio_commercial_sales'),
                    activeOn: ['studio_commercial_sales'],
                  },
                ],
              },
              {
                name: 'Studio Data Tables',
                label: t('AI.DATA_TABLES.SIDEBAR_LABEL'),
                icon: 'i-lucide-table-2',
                to: accountScopedRoute('studio_data_tables'),
                activeOn: ['studio_data_tables', 'studio_data_table_detail'],
              },
              {
                name: 'Studio External Tools',
                label: t('AI.EXTERNAL_TOOLS.SIDEBAR_LABEL'),
                icon: 'i-lucide-wrench',
                to: accountScopedRoute('studio_external_tools'),
                activeOn: ['studio_external_tools'],
              },
              {
                name: 'Studio Analytics',
                label: 'Analytics',
                icon: 'i-lucide-bar-chart-3',
                to: accountScopedRoute('studio_analytics'),
                activeOn: [
                  'studio_analytics',
                  'studio_analytics_overview',
                  'studio_analytics_detail',
                ],
                children: [
                  // Gráficos aninhado (mesmo desenho da Massiva) — deixou de
                  // ser aba dentro da página. As chaves de API saíram de vez:
                  // já vivem nas Configurações do Studio.
                  {
                    name: 'Studio Analytics Gráficos',
                    label: t('AI.ANALYTICS.CHARTS.SIDEBAR_LABEL'),
                    to: accountScopedRoute('studio_analytics_charts'),
                    activeOn: ['studio_analytics_charts'],
                  },
                  {
                    name: 'Studio Analytics Satisfação',
                    label: t('AI.ANALYTICS.SATISFACTION.SIDEBAR_LABEL'),
                    to: accountScopedRoute('studio_analytics_satisfaction'),
                    activeOn: ['studio_analytics_satisfaction'],
                  },
                ],
              },
            ],
          },
        ]
      : []),
    {
      name: 'Chamados',
      label: t('AI.CHAMADOS.SIDEBAR_LABEL'),
      icon: 'i-lucide-ticket',
      requiredPermissions: [CHAMADOS_VIEW_PERMISSIONS],
      // Chamados com novidade do Gestão que a pessoa ainda não abriu.
      getterKeys: {
        count: 'studioChamados/getNewsBadge',
      },
      children: [
        {
          name: 'ChamadosTodos',
          label: t('AI.CHAMADOS.SIDEBAR_ALL'),
          icon: 'i-lucide-list',
          to: accountScopedRoute('chamados'),
          activeOn: ['chamados', 'chamados_detail'],
        },
      ],
    },
    ...(isKanbanEnabled.value
      ? [
          {
            name: 'Kanban',
            label: t('SIDEBAR.KANBAN'),
            icon: 'i-lucide-kanban-square',
            // Degrau próprio do Kanban (helper/kanbanPermissions.js), não
            // mais settings_access: dar o quadro a um atendente não pode
            // exigir dar Configurações a ele.
            // 'agent' = atendente sem papel personalizado, que tem o quadro
            // por padrão — a rota (kanban.routes.js viewerMeta) já aceitava;
            // sem ele aqui o menu sumia e o quadro só abria pela URL.
            requiredPermissions: [
              'agent',
              ...kanbanRoutePermissions(KANBAN_ACCESS_PERMISSIONS),
            ],
            children: [
              {
                name: 'Kanban Board',
                label: t('SIDEBAR.KANBAN_BOARD'),
                icon: 'i-lucide-trello',
                to: accountScopedRoute('kanban_board'),
                activeOn: ['kanban_board'],
              },
              // Papel "só o quadro" (kanban_analytics_block) não vê o item.
              ...(canViewKanbanAnalytics.value
                ? [
                    {
                      name: 'Kanban Analytics',
                      label: t('SIDEBAR.KANBAN_ANALYTICS'),
                      icon: 'i-lucide-bar-chart-3',
                      to: accountScopedRoute('kanban_analytics'),
                      activeOn: ['kanban_analytics'],
                    },
                  ]
                : []),
              {
                name: 'Kanban Scheduled Actions',
                label: t('SIDEBAR.KANBAN_SCHEDULED_ACTIONS'),
                icon: 'i-lucide-alarm-clock',
                // Sem isto o item aparecia pra quem a rota barra.
                requiredPermissions: kanbanRoutePermissions(
                  KANBAN_MANAGE_PERMISSIONS
                ),
                to: accountScopedRoute('kanban_scheduled_actions'),
                activeOn: ['kanban_scheduled_actions'],
              },
            ],
          },
        ]
      : []),
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      requiredPermissions: [PORTAL_PERMISSIONS],
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          icon: 'i-lucide-file-text',
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          icon: 'i-lucide-folders',
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          icon: 'i-lucide-languages',
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          icon: 'i-lucide-settings',
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
        },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
          requiredPermissions: [AGENTS_ACCESS_PERMISSIONS],
          actions: [
            {
              label: t('AGENT_MGMT.HEADER_BTN_TXT'),
              icon: 'i-lucide-plus',
              action: 'new-agent',
            },
          ],
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
          actions: [
            {
              label: t('TEAMS_SETTINGS.NEW_TEAM'),
              icon: 'i-lucide-plus',
              to: accountScopedRoute('settings_teams_new'),
            },
          ],
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
          actions: [
            {
              label: t('SETTINGS.INBOXES.NEW_INBOX'),
              icon: 'i-lucide-plus',
              to: accountScopedRoute('settings_inbox_new'),
            },
          ],
        },
        {
          name: 'Settings Webchat',
          label: t('SIDEBAR.WEBCHAT'),
          icon: 'i-lucide-message-square-share',
          to: accountScopedRoute('webchat_settings_index'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
          actions: [
            {
              label: t('LABEL_MGMT.HEADER_BTN_TXT'),
              icon: 'i-lucide-plus',
              action: 'new-label',
            },
          ],
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
          actions: [
            {
              label: t('ATTRIBUTES_MGMT.HEADER_BTN_TXT'),
              icon: 'i-lucide-plus',
              action: 'new-attribute',
            },
          ],
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('automation_list'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
          actions: [
            {
              label: t('AUTOMATION.HEADER_BTN_TXT'),
              icon: 'i-lucide-plus',
              action: 'new-automation',
            },
          ],
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
          actions: [
            {
              label: t('AGENT_BOTS.ADD.TITLE'),
              icon: 'i-lucide-plus',
              action: 'new-agent-bot',
            },
          ],
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
          actions: [
            {
              label: t('MACROS.HEADER_BTN_TXT'),
              icon: 'i-lucide-plus',
              to: accountScopedRoute('macros_new'),
            },
          ],
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
          actions: [
            {
              label: t('CANNED_MGMT.HEADER_BTN_TXT'),
              icon: 'i-lucide-plus',
              action: 'new-canned',
            },
          ],
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
          actions: [
            {
              label: t('CUSTOM_ROLE.HEADER_BTN_TXT'),
              icon: 'i-lucide-plus',
              action: 'new-custom-role',
            },
          ],
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
          actions: [
            {
              label: t('SLA.ADD_ACTION'),
              icon: 'i-lucide-plus',
              action: 'new-sla',
            },
          ],
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
          requiredPermissions: [SETTINGS_ACCESS_PERMISSIONS],
        },
      ],
    },
  ];

  return items
    .map(item => {
      if (item.children) {
        // Filho sem requiredPermissions proprio herda o gate do pai — assim
        // filhos "default permitido" (ex: Telephony/WhatsApp Voice) nao
        // vazam pra agente restrito. Mas um filho com permissao independente
        // (ex: Agents via agents_access) continua acessivel mesmo sem o gate
        // do pai (ex: settings_access), mantendo o grupo visivel so com ele.
        const accessibleChildren = item.children.filter(child =>
          hasMenuPermission(
            child.requiredPermissions ?? item.requiredPermissions
          )
        );

        if (
          hasMenuPermission(item.requiredPermissions) ||
          accessibleChildren.length > 0
        ) {
          return {
            ...item,
            children: accessibleChildren,
          };
        }
        return null;
      }

      return hasMenuPermission(item.requiredPermissions) ? item : null;
    })
    .filter(item => item !== null);
});

const isItemActiveByRoute = item => {
  if (item.to) {
    if (route.name === item.to.name) return true;
    if (item.activeOn?.includes(route.name)) return true;
  }
  if (item.children) return item.children.some(isItemActiveByRoute);
  return false;
};

const selectedItem = computed(() =>
  menuItems.value.find(item => item.name === selectedItemName.value)
);

const togglePanel = name => {
  selectedItemName.value = selectedItemName.value === name ? null : name;
};

const router = useRouter();

// Actions emitidas pelo SidebarTreeNode (SidebarPanel propaga). Despachamos
// pros consumidores via event bus em createSidebarActionHandler — sem
// importar handlers que requeiram modais (mantém a sidebar desacoplada da
// UI da ChatList).
const handleSidebarAction = createSidebarActionHandler({
  router,
  emitter,
  accountScopedRoute,
  busEvents: BUS_EVENTS,
});

onMounted(() => {
  // A rota atual tem prioridade sobre o painel persistido. Assim, abrir uma
  // URL direta como /studio/settings sempre revela o item ativo no SidebarPanel.
  const match = menuItems.value.find(isItemActiveByRoute);
  if (match?.children) {
    selectedItemName.value = match.name;
    return;
  }

  const persisted = selectedItemName.value;
  if (persisted && menuItems.value.some(item => item.name === persisted)) {
    return;
  }
  selectedItemName.value = null;
});

watch(
  () => route.name,
  () => {
    // Sync painel com a rota só se já tinha um aberto
    if (selectedItemName.value === null) return;
    const match = menuItems.value.find(isItemActiveByRoute);
    if (match?.children && match.name !== selectedItemName.value) {
      selectedItemName.value = match.name;
    }
    // Em mobile, fechar o drawer após navegar
    if (props.isMobileSidebarOpen) {
      emit('closeMobileSidebar');
    }
  }
);
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-n-solid-2 rtl:border-l ltr:border-r border-n-weak flex flex-row flex-shrink-0 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 transition-transform duration-300 ease-in-out md:static md:ltr:translate-x-0 md:rtl:-translate-x-0"
    :class="{
      'shadow-lg md:shadow-none': isMobileSidebarOpen,
      'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
    }"
  >
    <nav class="flex flex-col w-14 py-2 overflow-y-auto no-scrollbar px-1">
      <ul class="flex flex-col items-center gap-1 m-0 list-none reset-base">
        <SidebarIconButton
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
          :is-panel-open="selectedItemName === item.name"
          @toggle="togglePanel"
        />
      </ul>
    </nav>

    <SidebarPanel
      v-if="selectedItem"
      :key="selectedItem.name"
      :item="selectedItem"
      @action="handleSidebarAction"
    />
  </aside>
</template>
